/**
 * Module: lib/imageResize
 * Role: Canvas-based image downscale + JPEG re-encode for creator selfie/screenshot
 *       upload (FTR-1106). Keeps payload under backend per-request size budget
 *       (verify_apply accepts base64 in JSON body — large iPhone photos at 4032×3024
 *       blow past nginx 8MB body limit before they reach FastAPI).
 *
 *       HEIC mitigation: iOS Safari auto-converts HEIC to JPEG when input is
 *       `accept="image/*"`, so we never see HEIC bytes here. If a file with
 *       MIME `image/heic`/`image/heif` somehow arrives, the browser's
 *       `createImageBitmap` will throw — caller surfaces "не удалось обработать
 *       фото, попробуйте JPEG/PNG".
 *
 * Uses: Web platform APIs (createImageBitmap, OffscreenCanvas|HTMLCanvasElement, FileReader)
 * Used by:
 *   - screens/creator/SelfieScreen.tsx (selfie capture)
 *   - screens/creator/ScreenshotScreen.tsx (TikTok stats screenshot)
 */

/** Maximum dimension (px) of the longest side after resize. */
export const MAX_DIMENSION_PX = 1280;

/** JPEG quality factor [0..1] passed to canvas.toBlob. */
export const JPEG_QUALITY = 0.85;

/** Hard ceiling — fail loud if post-resize blob exceeds this. */
export const MAX_OUTPUT_BYTES = 5 * 1024 * 1024; // 5 MB

/** Soft target — log warning when crossed but still return. */
export const TARGET_OUTPUT_BYTES = 1 * 1024 * 1024; // 1 MB

export interface ResizeResult {
  /** Base64 data URL: `data:image/jpeg;base64,...` ready to embed in JSON. */
  dataUrl: string;
  /** Decoded byte size of the JPEG (without data: prefix). */
  byteSize: number;
  /** Final width (px) after downscale. */
  width: number;
  /** Final height (px) after downscale. */
  height: number;
}

export class ImageResizeError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = 'ImageResizeError';
  }
}

/**
 * Resize `file` so the longest side ≤ MAX_DIMENSION_PX, re-encode as JPEG
 * at JPEG_QUALITY, and return as base64 data URL.
 *
 * If the input is already small (both dimensions ≤ MAX_DIMENSION_PX) AND
 * a JPEG, we still re-encode through canvas to strip EXIF — privacy first,
 * GPS coordinates in EXIF are a leak surface for creator selfies.
 *
 * Throws ImageResizeError when:
 *   - File cannot be decoded (corrupt / unsupported MIME like HEIC on non-iOS).
 *   - Post-resize blob exceeds MAX_OUTPUT_BYTES (≥5MB after 1280px JPEG @0.85
 *     means upstream pipeline is broken, not a user-error to swallow).
 */
export async function resizeImage(file: File): Promise<ResizeResult> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch (err) {
    throw new ImageResizeError(
      'Не удалось прочитать изображение. Попробуйте JPEG или PNG.',
      err,
    );
  }

  const { width: srcW, height: srcH } = bitmap;
  const scale = Math.min(1, MAX_DIMENSION_PX / Math.max(srcW, srcH));
  const dstW = Math.round(srcW * scale);
  const dstH = Math.round(srcH * scale);

  const canvas = createCanvas(dstW, dstH);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close?.();
    throw new ImageResizeError('Canvas 2D context недоступен.');
  }
  ctx.drawImage(bitmap, 0, 0, dstW, dstH);
  bitmap.close?.();

  const blob = await canvasToJpegBlob(canvas, JPEG_QUALITY);
  if (blob.size > MAX_OUTPUT_BYTES) {
    throw new ImageResizeError(
      `Фото слишком большое после сжатия (${(blob.size / 1024 / 1024).toFixed(1)} MB). ` +
        `Попробуйте другое фото.`,
    );
  }
  if (blob.size > TARGET_OUTPUT_BYTES) {
    console.warn(
      `[imageResize] post-resize ${(blob.size / 1024).toFixed(0)}KB > target ` +
        `${TARGET_OUTPUT_BYTES / 1024}KB at ${dstW}×${dstH}`,
    );
  }

  const dataUrl = await blobToDataUrl(blob);
  return { dataUrl, byteSize: blob.size, width: dstW, height: dstH };
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

type AnyCanvas = HTMLCanvasElement | OffscreenCanvas;

function createCanvas(width: number, height: number): AnyCanvas {
  if (typeof OffscreenCanvas !== 'undefined') {
    return new OffscreenCanvas(width, height);
  }
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  return c;
}

async function canvasToJpegBlob(canvas: AnyCanvas, quality: number): Promise<Blob> {
  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob({ type: 'image/jpeg', quality });
  }
  return new Promise<Blob>((resolve, reject) => {
    (canvas as HTMLCanvasElement).toBlob(
      (b) =>
        b
          ? resolve(b)
          : reject(new ImageResizeError('canvas.toBlob() returned null')),
      'image/jpeg',
      quality,
    );
  });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') resolve(result);
      else reject(new ImageResizeError('FileReader returned non-string result'));
    };
    reader.onerror = () =>
      reject(new ImageResizeError('FileReader failed', reader.error));
    reader.readAsDataURL(blob);
  });
}
